import React from 'react';
import PropTypes from 'prop-types';
import Button from '@deliveroo/consumer-component-library/components/Button';
import Heading from '@deliveroo/consumer-component-library/components/Heading';
import P from '@deliveroo/consumer-component-library/components/P';
import Modal from '@deliveroo/consumer-component-library/components/Modal';

import i18n from '../../utils/i18n';

import OrderRatingIssues from '../OrderRatingIssues';
import OrderRatingStars from '../OrderRatingStars';
import OrderRatingComment from '../OrderRatingComment';

import styles from './styles.scss';

const OrderRatingModal = ({
  ratingStrings,
  selectedRating,
  onRatingStarsChange,
  showRatingIssues,
  showCommentBox,
  selectedRatingIssues,
  ratingComment,
  onRatingIssuesChange,
  onRatingCommentChange,
  isButtonDisabled,
  isLowRatingSelected,
  ratingOptions,
  cancel,
  onSubmit,
  isOpen,
  appElement,
}) => (
  <Modal
    appElement={appElement}
    headerTitle={ratingStrings.title}
    isOpen={isOpen}
    type="fullscreen"
    renderFooter={() => (
      <Button isBlock isDisabled={isButtonDisabled} onClick={onSubmit}>
        {i18n.gettext('Send')}
      </Button>
    )}
    onClose={cancel}
    closeButtonLabel={i18n.gettext('closeButton.label')}
  >
    <div className={styles.stars}>
      <Heading level={3} size="x-small">
        {ratingStrings.heading}
      </Heading>

      <P color="grey" spacing={['small', null, 'medium', null]}>
        {ratingStrings.subheading}
      </P>

      <OrderRatingStars selectedRating={selectedRating} onChange={onRatingStarsChange} />
    </div>

    {showRatingIssues && (
      <OrderRatingIssues
        selectedIssues={selectedRatingIssues}
        onChange={onRatingIssuesChange}
        options={ratingOptions}
      />
    )}

    {showCommentBox && (
      <OrderRatingComment
        questionLowRating={ratingStrings.commentQuestionLowRating}
        questionHighRating={ratingStrings.commentQuestionHighRating}
        placeholderLowRating={ratingStrings.commentPlaceholderLowRating}
        placeholderHighRating={ratingStrings.commentPlaceholderHighRating}
        characterLimitError={ratingStrings.characterLimitError}
        ratingComment={ratingComment}
        onChange={onRatingCommentChange}
        isLowRatingSelected={isLowRatingSelected}
      />
    )}
  </Modal>
);

OrderRatingModal.propTypes = {
  /**
    Selector to specify the portion of your app that should be hidden (via aria-hidden) to
    prevent assistive technologies from reading content outside of this modal.
  */
  appElement: PropTypes.string,
  /** Strings to display as modal copy */
  ratingStrings: PropTypes.shape({
    title: PropTypes.string.isRequired,
    heading: PropTypes.string.isRequired,
    subheading: PropTypes.string.isRequired,
    commentQuestionLowRating: PropTypes.string.isRequired,
    commentQuestionHighRating: PropTypes.string.isRequired,
    commentPlaceholderLowRating: PropTypes.string.isRequired,
    commentPlaceholderHighRating: PropTypes.string.isRequired,
    characterLimitError: PropTypes.string.isRequired,
  }).isRequired,
  /** Number of stars selected */
  selectedRating: PropTypes.string.isRequired,
  /** Event handler for when rating is selected */
  onRatingStarsChange: PropTypes.func.isRequired,
  /** Whether to show the rating issues component */
  showRatingIssues: PropTypes.bool,
  /** Whether to show the comment box component */
  showCommentBox: PropTypes.bool,
  /** Issues that are selected */
  selectedRatingIssues: PropTypes.arrayOf(PropTypes.string),
  /** Text entered in the comment box */
  ratingComment: PropTypes.string,
  /** Event handler for when issues are selected */
  onRatingIssuesChange: PropTypes.func.isRequired,
  /** Event handler for when text is entered in comment box */
  onRatingCommentChange: PropTypes.func.isRequired,
  /** Whether the selected rating is low */
  isLowRatingSelected: PropTypes.bool,
  /** Whether submit button is disabled */
  isButtonDisabled: PropTypes.bool.isRequired,
  /** A list of feedback for improvement options */
  ratingOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      icon: PropTypes.elementType.isRequired, // eslint-disable-line react/no-typos
    }),
  ).isRequired,
  /** Function to close the modal */
  cancel: PropTypes.func.isRequired,
  /** Function to submit rating */
  onSubmit: PropTypes.func.isRequired,
  /** Whether the modal is open or not */
  isOpen: PropTypes.bool,
};

OrderRatingModal.defaultProps = {
  appElement: '#__next',
  showRatingIssues: false,
  showCommentBox: false,
  selectedRatingIssues: [],
  ratingComment: '',
  isLowRatingSelected: false,
  isOpen: false,
};

export default OrderRatingModal;
